import React, { useCallback, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  Button,
  Divider,
  IconButton,
} from "@material-ui/core";
import { getFieldProps, inputProps } from "../../utils";
import styled from "styled-components/macro";
import { useFormikContext } from "formik";
import MarkdownEditorV2 from "../../shared/MarkdownEditorV2";

import InfoTooltip from "../../InfoTooltip";
import ImageUpload from "../../ImageUpload";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { uploadFile } from "../../../api";
import bytes from "bytes";
import FileUpload from "../../shared/FileUpload";
import { DocumentContainer } from "./Doubts";

const CustomTypography = styled(Typography)`
  font-size: 1rem;
  margin-top: 1.5rem;
`;

const BuildMarkdownEditor = ({ formik, placeholder, name, value }) => {
  const onChange = useCallback(
    (value) => {
      formik.setFieldValue(name, value);
      formik.setFieldTouched(name, true);
    },
    [formik, name]
  );

  return (
    <Grid item md={6} xs={12}>
      <MarkdownEditorV2
        error={false}
        label={``}
        placeHolder={placeholder}
        {...getFieldProps(formik, {
          name,
        })}
        value={value}
        onChange={onChange}
      />
    </Grid>
  );
};

const NewsText = () => {
  const formik = useFormikContext();
  const [isFileUploading, setIsFileUploading] = useState(null);

  const handleDocumentChange = useCallback(async (args, formik, field) => {
    setIsFileUploading(field);

    const data = await uploadFile({
      ...args,
      customPathInfo: { cmp: formik.values?.cmp },
    });

    setIsFileUploading(null);
    formik.setFieldValue(field, { name: args.name, url: data.url });
    formik.setFieldTouched(field, true);
  }, []);

  const handleDocumentReset = useCallback(
    async (field) => {
      formik.setFieldValue(`${field}`, { name: null, url: null });
      formik.setFieldTouched(`${field}`, true);
    },
    [formik]
  );

  const handleAddDocument = useCallback(() => {
    const defaultDocuments = formik.values.text_default?.documents ?? [];
    const englishDocuments = formik.values.text_en?.documents ?? [];

    const updatedDefaultValues = [
      ...defaultDocuments,
      {
        name: null,
        url: null,
      },
    ];

    const updatedEnValues = [
      ...englishDocuments,
      {
        name: null,
        url: null,
      },
    ];

    formik.setFieldValue("text_default.documents", updatedDefaultValues);
    formik.setFieldValue("text_en.documents", updatedEnValues);
  }, [formik]);

  const handleDeleteDocument = useCallback(
    (i) => () => {
      const newDefaultValue = [...(formik.values.text_default.documents || [])];
      const newEnValue = [...(formik.values.text_en.documents || [])];

      newDefaultValue.splice(i, 1);
      newEnValue.splice(i, 1);

      formik.setFieldValue("text_default.documents", newDefaultValue);
      formik.setFieldValue("text_en.documents", newEnValue);

      const hasDefaultDocs = newDefaultValue.some(
        (doc) => doc.url && doc.url !== null
      );
      const hasEnDocs = newEnValue.some((doc) => doc.url && doc.url !== null);

      if (!hasDefaultDocs) {
        formik.setFieldValue("text_default.textbeforefiles", "");
      }
      if (!hasEnDocs) {
        formik.setFieldValue("text_en.textbeforefiles", "");
      }
    },
    [formik]
  );

  const handleImageChange = useCallback(
    ({ imgFieldName, url }) => {
      console.log("url", url);
      formik.setFieldValue(imgFieldName, url);
      formik.setFieldTouched(imgFieldName, true);
    },
    [formik]
  );

  const handleImageReset = useCallback(
    ({ imgFieldName }) => {
      formik.setFieldValue(imgFieldName, null);
      formik.setFieldTouched(imgFieldName, true);
    },
    [formik]
  );
  const buildDocumentUpload = useCallback(
    (formik, caption, name, formikValue) => {
      return (
        <Grid item md={5} xs={11}>
          <FileUpload
            accept=".pdf, .doc, .odt, .xlsx, .xls, .ods"
            maxSize={bytes("10 MB")}
            readAs="DataURL"
            onChange={(val) => handleDocumentChange(val, formik, name)}
          >
            {({ upload, onUpload, error }) => {
              return (
                <>
                  <DocumentContainer
                    buttonProps={{ children: "Upload", fullWidth: true }}
                    caption={caption}
                    containerStyles={{}}
                    disabled={false}
                    document={formikValue}
                    error={error}
                    handleRemove={() => handleDocumentReset(name)}
                    isLoading={isFileUploading === name}
                    isUploading={isFileUploading === name}
                    styles={{}}
                    onStartUpload={upload}
                    onUpload={onUpload}
                  />
                </>
              );
            }}
          </FileUpload>
        </Grid>
      );
    },
    [handleDocumentChange, handleDocumentReset, isFileUploading]
  );

  const buildTextField = (
    formik,
    placeholder,
    name,
    label,
    md = 5,
    xs = 11,
    required = false
  ) => {
    return (
      <Grid item md={md} xs={xs}>
        <TextField
          fullWidth
          InputLabelProps={{ shrink: true }}
          InputProps={inputProps}
          label={label}
          margin="none"
          placeholder={placeholder}
          required={required}
          variant="outlined"
          {...getFieldProps(formik, {
            name,
          })}
        />
      </Grid>
    );
  };
  return (
    <Grid item xs={12}>
      <Grid item>
        <Typography variant="h6">
          In this section you should write down all the information to
          understand what the news is about!
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <>
          <Grid item md={12} xs={12}>
            <Grid
              container
              alignItems=""
              spacing={2}
              style={{ flexDirection: "row" }}
            >
              <Grid item md={4} xs={4}>
                <Grid
                  container
                  alignItems=""
                  spacing={2}
                  style={{ flexDirection: "column" }}
                >
                  <Grid item xs={12}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        <CustomTypography color="textSecondary" variant="h6">
                          Upload a picture before the introduction
                        </CustomTypography>
                      </Grid>
                      <Grid item>
                        <InfoTooltip
                          text="Upload .jpg or .png files. The uploaded picture will be displayed with width as a % of the document body (by default 50%).
                  The picture will be scaled vertically to maintain proportions."
                          title="Image Dimensions"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item md={12} xs={12}>
                    <ImageUpload
                      buttonProps={{
                        children: "Upload",
                      }}
                      caption={
                        <Typography
                          color="textSecondary"
                          style={{ color: "#BFBFBF" }}
                          variant="body2"
                        >
                          Upload picture before the introduction (.png, .jpg,
                          .jpeg)
                        </Typography>
                      }
                      containerStyles={{ height: 250 }}
                      customPathInfo={{ cmp: formik.values?.cmp }}
                      errorMessage={
                        getFieldProps(formik, { name: "imgbeforeintro" })
                          .helperText
                      }
                      maxSize={1.5 * 1024 * 1024} //1.5MB
                      styles={{ maxHeight: 250, width: "unset" }}
                      value={formik.values.imgbeforeintro}
                      onChange={(obj) =>
                        handleImageChange({
                          ...obj,
                          imgFieldName: "imgbeforeintro",
                        })
                      }
                      onReset={(obj) =>
                        handleImageReset({
                          ...obj,
                          imgFieldName: "imgbeforeintro",
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item md={2} xs={2}>
                <Grid
                  container
                  alignItems="center"
                  direction="row"
                  justifyContent="center"
                  style={
                    formik.values.imgbeforeintro
                      ? { height: "100%" }
                      : { paddingTop: 64 }
                  }
                >
                  <Grid item>
                    <TextField
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      label={"Img width in %"}
                      placeholder="e.g.: 50%"
                      variant="outlined"
                      {...getFieldProps(formik, {
                        defaultValue: formik.values.imgbeforeintropercent,
                        name: "imgbeforeintropercent",
                      })}
                    ></TextField>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <CustomTypography color="textSecondary" variant="h6">
              Write a brief introduction! *
            </CustomTypography>
          </Grid>
          <Grid container spacing={2}>
            <BuildMarkdownEditor
              formik={formik}
              name={`text_default.summary`}
              placeholder={"Default language"}
              value={formik.values.text_default.summary}
            />
            <BuildMarkdownEditor
              formik={formik}
              name={`text_en.summary`}
              placeholder={"English"}
              value={formik.values.text_en.summary}
            />
          </Grid>
        </>
        <>
          <Grid item md={12} xs={12}>
            <Grid
              container
              alignItems=""
              spacing={2}
              style={{ flexDirection: "row" }}
            >
              <Grid item md={4} xs={4}>
                <Grid
                  container
                  alignItems=""
                  spacing={2}
                  style={{ flexDirection: "column" }}
                >
                  <Grid item xs={12}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        <CustomTypography color="textSecondary" variant="h6">
                          Upload a picture before the main text
                        </CustomTypography>
                      </Grid>
                      <Grid item>
                        <InfoTooltip
                          text="Upload .jpg or .png files. The uploaded picture will be displayed with width as a % of the document body (by default 50%).
                  The picture will be scaled vertically to maintain proportions."
                          title="Image Dimensions"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item md={12} xs={12}>
                    <ImageUpload
                      buttonProps={{
                        children: "Upload",
                      }}
                      caption={
                        <Typography
                          color="textSecondary"
                          style={{ color: "#BFBFBF" }}
                          variant="body2"
                        >
                          Upload a picture before the main text (.png, .jpg,
                          .jpeg)
                        </Typography>
                      }
                      containerStyles={{ height: 250 }}
                      customPathInfo={{ cmp: formik.values?.cmp }}
                      errorMessage={
                        getFieldProps(formik, { name: "imgbeforemain" })
                          .helperText
                      }
                      maxSize={1.5 * 1024 * 1024} //1.5MB
                      styles={{ maxHeight: 250, width: "unset" }}
                      value={formik.values.imgbeforemain}
                      onChange={(obj) =>
                        handleImageChange({
                          ...obj,
                          imgFieldName: "imgbeforemain",
                        })
                      }
                      onReset={(obj) =>
                        handleImageReset({
                          ...obj,
                          imgFieldName: "imgbeforemain",
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item md={2} xs={2}>
                <Grid
                  container
                  alignItems="center"
                  direction="row"
                  justifyContent="center"
                  style={
                    formik.values.imgbeforemain
                      ? { height: "100%" }
                      : { paddingTop: 64 }
                  }
                >
                  <Grid item>
                    <TextField
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      label={"Img width in %"}
                      placeholder="e.g.: 50%"
                      variant="outlined"
                      {...getFieldProps(formik, {
                        defaultValue: formik.values.imgbeforemainpercent,
                        name: "imgbeforemainpercent",
                      })}
                    ></TextField>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item style={{ marginTop: "2rem" }}>
            <CustomTypography color="textSecondary" variant="h6">
              Write the text of the news! *
            </CustomTypography>
          </Grid>
          <Grid container spacing={2}>
            <BuildMarkdownEditor
              formik={formik}
              name={`text_default.text`}
              placeholder={"Default language"}
              value={formik.values.text_default.text}
            />
            <BuildMarkdownEditor
              formik={formik}
              name={`text_en.text`}
              placeholder={"English"}
              value={formik.values.text_en.text}
            />
          </Grid>
        </>
        <Grid item md={12} xs={12}>
          <Grid
            container
            alignItems=""
            spacing={2}
            style={{ flexDirection: "row" }}
          >
            <Grid item md={4} xs={4}>
              <Grid
                container
                alignItems=""
                spacing={2}
                style={{ flexDirection: "column" }}
              >
                <Grid item xs={12}>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item>
                      <CustomTypography color="textSecondary" variant="h6">
                        Upload a picture at the end of the news
                      </CustomTypography>
                    </Grid>
                    <Grid item>
                      <InfoTooltip
                        text="Upload .jpg or .png files. The uploaded picture will be displayed with width as a % of the document body (by default 50%).
                  The picture will be scaled vertically to maintain proportions."
                        title="Image Dimensions"
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item md={12} xs={12}>
                  <ImageUpload
                    buttonProps={{
                      children: "Upload",
                    }}
                    caption={
                      <Typography
                        color="textSecondary"
                        style={{ color: "#BFBFBF" }}
                        variant="body2"
                      >
                        Upload a picture at the end of the news (.png, .jpg,
                        .jpeg)
                      </Typography>
                    }
                    containerStyles={{ height: 250 }}
                    customPathInfo={{ cmp: formik.values?.cmp }}
                    errorMessage={
                      getFieldProps(formik, { name: "imgendofnews" }).helperText
                    }
                    maxSize={1.5 * 1024 * 1024} //1.5MB
                    styles={{ maxHeight: 250, width: "unset" }}
                    value={formik.values.imgendofnews}
                    onChange={(obj) =>
                      handleImageChange({
                        ...obj,
                        imgFieldName: "imgendofnews",
                      })
                    }
                    onReset={(obj) =>
                      handleImageReset({
                        ...obj,
                        imgFieldName: "imgendofnews",
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item md={2} xs={2}>
              <Grid
                container
                alignItems="center"
                direction="row"
                justifyContent="center"
                style={
                  formik.values.imgendofnews
                    ? { height: "100%" }
                    : { paddingTop: 64 }
                }
              >
                <Grid item>
                  <TextField
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    label={"Img width in %"}
                    placeholder="e.g.: 50%"
                    variant="outlined"
                    {...getFieldProps(formik, {
                      defaultValue: formik.values.imgendofnewspercent,
                      name: "imgendofnewspercent",
                    })}
                  ></TextField>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item style={{ marginBottom: "0.5rem", marginTop: "2rem" }}>
          <Typography
            color="textSecondary"
            style={{ fontSize: "1rem" }}
            variant="h6"
          >
            Want to upload a video? Upload it here!
          </Typography>
        </Grid>
        <Grid container spacing={2}>
          {buildTextField(
            formik,
            "Upload video url",
            `text_default.video`,
            "Default language",
            6,
            12
          )}
          {buildTextField(
            formik,
            "Upload video url",
            `text_en.video`,
            "English",
            6,
            12
          )}
        </Grid>
        {(formik.values.text_default?.documents?.some((doc) => doc.url) ||
          formik.values.text_en?.documents?.some((doc) => doc.url)) && (
          <>
            <Grid item style={{ marginBottom: "1rem", marginTop: "1rem" }}>
              <Typography
                color="textSecondary"
                style={{ fontSize: "0.875rem" }}
                variant="h6"
              >
                Text to be displayed before the files
              </Typography>
            </Grid>
            <Grid container spacing={2}>
              {buildTextField(
                formik,
                "Text before files...",
                `text_default.textbeforefiles`,
                "Default language",
                6,
                12
              )}
              {buildTextField(
                formik,
                "Text before files...",
                `text_en.textbeforefiles`,
                "English",
                6,
                12
              )}
            </Grid>
          </>
        )}
        <Grid item style={{ marginBottom: "0.5rem", marginTop: "2rem" }}>
          <Typography
            color="textSecondary"
            style={{ fontSize: "1rem" }}
            variant="h6"
          >
            Is there any supporting document? Upload it here!
          </Typography>
        </Grid>

        {formik.values.text_default?.documents?.map((_, i) => {
          return (
            <>
              <Grid key={i} container spacing={2}>
                {buildDocumentUpload(
                  formik,
                  "Upload document(s) (.pdf, .word, .odt, .xlsx, .xls, .ods) supporting single benefit (Default language)",
                  `text_default.documents[${i}]`,
                  formik.values.text_default?.documents[i] ?? {
                    name: null,
                    url: null,
                  }
                )}
                {buildDocumentUpload(
                  formik,
                  "Upload document(s) (.pdf, .word, .odt, .xlsx, .xls, .ods) supporting single benefit (English)",
                  `text_en.documents[${i}]`,
                  formik.values.text_en?.documents[i] ?? {
                    name: null,
                    url: null,
                  }
                )}
                <Grid item md={2} xs={1}>
                  <IconButton onClick={handleDeleteDocument(i)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
              {formik.values.text_default?.documents &&
                formik.values.text_default?.documents?.length &&
                i < formik.values.text_default?.documents?.length - 1 && (
                  <Grid item md={11} xs={11}>
                    <Divider style={{ marginBlock: "1rem" }} />
                  </Grid>
                )}
            </>
          );
        })}
        <Grid
          item
          style={{ marginBottom: "1rem", marginTop: "1.5rem" }}
          xs={12}
        >
          <Button
            color="primary"
            disabled={false}
            endIcon={<AddIcon />}
            variant="contained"
            onClick={handleAddDocument}
          >
            Add document
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default NewsText;
